import { AvatarWithDetails } from '#app/components/Avatars/AvatarWithDetails/AvatarWithDetails.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useHorizontalFades } from '#app/hooks/useHorizontalFades.js';
import { type ExpandedCollaboratorsListData } from './types.js';
import { ChevronUpIcon } from '@contra/icons/icons';
import { css, styled } from 'styled-components';

type ExpandedCollaboratorsListProps = {
  readonly collaborators: ExpandedCollaboratorsListData;
  readonly onSeeLess: () => void;
};

const CollaboratorListContainer = styled.div`
  ${({ theme }) => css`
    padding: 0 0 16px;
    border-bottom: 1px solid ${theme.colorsV2.gray30};
  `}
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TextButton = styled.button`
  display: flex;
  align-items: center;

  &:hover,
  &:focus {
    opacity: 0.6;
  }

  > svg {
    margin-left: 2px;
  }

  transition: opacity 0.2s ease-in-out;
`;

const CollaboratorsRow = styled.ul`
  overflow-x: auto;
  padding-bottom: 16px;
  white-space: nowrap;
  scroll-snap-type: x mandatory;

  & > * + * {
    margin-left: 48px;
  }

  & > :last-child {
    margin-right: 48px;
  }
`;

const Collaborator = styled.li`
  display: inline-block;
  vertical-align: top;
  scroll-snap-align: start;
`;

export const ExpandedCollaboratorsList = ({
  collaborators,
  onSeeLess,
}: ExpandedCollaboratorsListProps) => {
  const { fadeLeft, fadeRight, HorizontalFades, onScroll } = useHorizontalFades(
    { fadeRightInitial: true },
  );

  if (!collaborators.length) return null;

  return (
    <CollaboratorListContainer>
      <HeaderRow>
        <Text
          color="gray60"
          textStyle="bodySmall"
        >
          In Collaboration With
        </Text>
        <TextButton onClick={onSeeLess}>
          <Text textStyle="subtitleSmall">
            See Less
            <ChevronUpIcon size={20} />
          </Text>
        </TextButton>
      </HeaderRow>

      <HorizontalFades
        color="gray10"
        fadeLeft={fadeLeft}
        fadeRight={fadeRight}
        fadeWidth={64}
      >
        <CollaboratorsRow onScroll={onScroll}>
          {collaborators.map((collaborator) => {
            if (collaborator.type === 'existing') {
              return (
                <Collaborator key={collaborator.id}>
                  <AvatarWithDetails
                    avatarImage={collaborator.avatarImage}
                    displayUsername={collaborator.displayUsername}
                    roles={collaborator.roles}
                    size="lg"
                    userName={collaborator.userName}
                  />
                </Collaborator>
              );
            } else if (collaborator.type === 'new') {
              return (
                <Collaborator key={collaborator.name}>
                  <AvatarWithDetails
                    roles={collaborator.roles}
                    size="lg"
                    userName={collaborator.name}
                  />
                </Collaborator>
              );
            }

            return null;
          })}
        </CollaboratorsRow>
      </HorizontalFades>
    </CollaboratorListContainer>
  );
};
